import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	input,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	QueryList,
	signal,
	Signal,
	ViewChild,
	ViewChildren,
	WritableSignal
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { EEntityDocumentCacheData } from "@calaosoft/osapp-common/entities/models/eentity-document-data";
import { Entity } from "@calaosoft/osapp-common/entities/models/entity";
import { IEntityDocumentCache } from "@calaosoft/osapp-common/entities/models/ientity-document-cache";
import { ObservableArray } from "@calaosoft/osapp-common/observable/models/observable-array";
import { ObservableProperty } from "@calaosoft/osapp-common/observable/models/observable-property";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { StoreHelper } from "@calaosoft/osapp-common/store/helpers/store-helper";
import { ICacheData } from "@calaosoft/osapp-common/store/models/icache-data";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { StringHelper } from "@calaosoft/osapp-common/utils/helpers/stringHelper";
import { DragScrollComponent, DragScrollItemDirective } from "ngx-drag-scroll";
import { defer, filter, map, Observable, of, Subject, switchMap, takeWhile } from "rxjs";
import { ComponentBase } from "../../../../helpers/ComponentBase";
import { DragScrollHelper } from "../../../../helpers/drag-scroll.helper";
import { FileHelper } from "../../../../helpers/fileHelper";
import { EGalleryCommand } from "../../../../model/gallery/EGalleryCommand";
import { GalleryFile } from "../../../../model/gallery/gallery-file";
import { IGalleryCommand } from "../../../../model/gallery/IGalleryCommand";
import { IPopoverItemParams } from "../../../../model/popover/IPopoverItemParams";
import { GalleryService } from "../../../../services/gallery.service";
import { ShowMessageParamsPopup } from "../../../../services/interfaces/ShowMessageParamsPopup";
import { PlatformService } from "../../../../services/platform.service";
import { PopoverService } from "../../../../services/popover.service";
import { UiMessageService } from "../../../../services/uiMessage.service";
import { EntitiesService } from "../../../entities/services/entities.service";
import { DmsDocument } from "../../models/dms-document";
import { Document } from "../../models/document";
import { FolderContent } from "../../models/folder-content";
import { DocExplorerDocumentsService } from "../../services/doc-explorer-documents.service";

@Component({
	selector: "calao-document-gallery",
	templateUrl: "./document-gallery.component.html",
	styleUrls: ["./document-gallery.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DocumentGalleryComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {
	//#region FIELDS

	private readonly moObservablePopOverItemsParams = new ObservableArray<IPopoverItemParams>([]);

	private readonly moGalleryCommandSubject = new Subject<IGalleryCommand>();

	private static readonly C_DEFAULT_GALLERY_FILES_TYPE = "application/pdf";

	protected readonly moDocumentsChanged: OutputEmitterRef<Document[]> = output({ alias: "documentsChanged" });

	//#endregion FIELDS

	//#region PROPERTIES

	/** Modèle */
	public readonly model: Signal<Entity> = input.required<Entity>();
	/** Chemin du dossier à afficher. */
	public readonly path: Signal<string | undefined> = input.required();
	private readonly path$ = toObservable(this.path);
	/** Indique si on doit afficher le contenu en lecture seule. */
	public readonly readonly: Signal<boolean> = input(false);
	private readonly readonly$ = toObservable(this.readonly);

	public readonly label: Signal<string | undefined> = input();

	/** Documents du dossier à afficher. */
	public readonly documents: WritableSignal<Document[]> = signal<Document[]>([]);
	/** Chemin du dossier à afficher. */
	public readonly folderPath: WritableSignal<string> = signal<string>("");

	public files: WritableSignal<GalleryFile[]> = signal<GalleryFile[]>([]);
	public readonly galleryCommand$: Observable<IGalleryCommand>;
	public galleryAcceptFiles = DocumentGalleryComponent.C_DEFAULT_GALLERY_FILES_TYPE;

	@ViewChild("dragScroll") public dragScroll?: DragScrollComponent;
	@ViewChild("dragScroll", { read: ElementRef }) public dragScrollElement?: ElementRef<HTMLElement>;
	@ViewChildren(DragScrollItemDirective, { read: ElementRef }) public dragScrollDirectives?: QueryList<
		ElementRef<HTMLElement>
	>;

	/** Indique si le boutton de défilement vers la gauche doit être caché */
	public observableHidLeftBtn = new ObservableProperty<boolean>(true);
	/** Indique si le boutton de défilement vers la droite doit être caché */
	public observableHidRightBtn = new ObservableProperty<boolean>(true);

	public get isMobile(): boolean {
		return this.isvcPlatform.isMobile;
	}

	//#endregion PROPERTIES

	//#region METHODS

	constructor(
		private readonly isvcDocExplorerDocuments: DocExplorerDocumentsService,
		private readonly isvcGallery: GalleryService,
		private readonly isvcEntities: EntitiesService,
		private readonly isvcPlatform: PlatformService,
		private readonly isvcPopover: PopoverService,
		private readonly isvcUiMessage: UiMessageService,
		poChangeDetectorRef: ChangeDetectorRef
	) {
		super(poChangeDetectorRef);

		this.galleryCommand$ = this.moGalleryCommandSubject.asObservable();

		toObservable(this.documents)
			.pipe(
				secure(this),
				map((paDocuments: Document[]) => {
					this.moDocumentsChanged.emit(paDocuments);
				})
			)
			.subscribe();
	}

	public ngOnInit(): void {
		this.readonly$
			.pipe(
				switchMap((pbReadonly: boolean) => {
					return this.path$.pipe(
						switchMap((psCurrentPath: string | undefined) => {
							if (!StringHelper.isBlank(psCurrentPath)) {
								return this.isvcDocExplorerDocuments.getFolderContent$(psCurrentPath).pipe(
									takeWhile((_, pnIndex: number) => pbReadonly || pnIndex === 0, true),
									map((poFolder?: FolderContent) => {
										this.documents.set(poFolder?.documents ?? []);
										this.folderPath.set(poFolder?.current.path ?? psCurrentPath);
										return ArrayHelper.hasElements(poFolder?.documents);
									})
								);
							}
							return of(false);
						}),
						filter((pbDocumentsLoaded) => !pbDocumentsLoaded) // Évite de réafficher les documents supprimés en cache.
					);
				})
			)
			.subscribe();

		this.moObservablePopOverItemsParams.resetArray([
			{
				action: () => of(this.moGalleryCommandSubject.next({ type: EGalleryCommand.takePicture })),
				color: "primary",
				icon: "camera",
				title: "Appareil photo"
			},
			{
				action: () => of(this.moGalleryCommandSubject.next({ type: EGalleryCommand.pickImage })),
				color: "primary",
				icon: "images",
				title: "Galerie"
			},
			{
				action: () => of(this.moGalleryCommandSubject.next({ type: EGalleryCommand.pickFile })),
				color: "primary",
				icon: "document-attach",
				title: "Fichiers"
			}
		]);
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		if (this.dragScroll) {
			this.dragScroll["isScrollReachesRightEnd"] = () => DragScrollHelper.hasReachedRightEnd(this.dragScroll);
			if (this.dragScrollElement) this.disableTouchPropagation(this.dragScrollElement);
			this.dragScrollDirectives?.forEach((poElementRef: ElementRef<HTMLElement>) =>
				this.disableTouchPropagation(poElementRef)
			);
		}
	}

	public override ngOnDestroy(): void {
		this.moGalleryCommandSubject.complete();
		super.ngOnDestroy();
	}

	public isImage(poItem: Document | GalleryFile): boolean {
		if (poItem instanceof DmsDocument || poItem instanceof GalleryFile)
			return FileHelper.isImageFile(FileHelper.getFileExtensionFromFileName(poItem.name)?.toLowerCase());
		return false;
	}

	public getImageGuid(poDocument: Document): string | undefined {
		if (poDocument instanceof DmsDocument) return poDocument.guid;
		return undefined;
	}

	public async openDocumentAsync(poDocument: Document): Promise<void> {
		await this.openDocument$(poDocument).pipe(secure(this)).toPromise();
	}

	private openDocument$(poDocument: Document): Observable<boolean> {
		return defer(() => {
			if (poDocument instanceof Entity) {
				return this.isvcEntities.navigateToEntityViewAsync(poDocument);
			} else return this.isvcGallery.openFile(poDocument);
		});
	}

	public presentPopOver(poEvent: MouseEvent): void {
		this.isvcPopover.showPopover(this.moObservablePopOverItemsParams, poEvent);
	}

	/** Événement qui survient lors d'un changement de fichier dans la galerie.
	 * @param paFiles Fichiers de la galerie
	 */
	public onFilesChanged(paFiles: GalleryFile[]): void {
		this.files.set(paFiles);

		const loModelCacheData: ICacheData | undefined = StoreHelper.getDocumentCacheData(this.model());
		const laDocumentsCache: IEntityDocumentCache[] = loModelCacheData?.documents ?? [];

		for (const poFile of paFiles) {
			if (!laDocumentsCache.some((poDoc: IEntityDocumentCache) => poFile.guid === poDoc.document.guid)) {
				laDocumentsCache.push({
					cacheDataState: EEntityDocumentCacheData.Add,
					document: poFile,
					path: this.folderPath()
				});
			}
		}

		StoreHelper.updateDocumentCacheData(this.model(), { documents: laDocumentsCache });
	}

	public onRemoveFileClickedAsync(poFile: GalleryFile): void {
		const loModelCacheData: ICacheData | undefined = StoreHelper.getDocumentCacheData(this.model());
		const laDocumentsCache: IEntityDocumentCache[] = loModelCacheData?.documents ?? [];

		const lnIndex: number = laDocumentsCache.findIndex(
			(poDoc: IEntityDocumentCache) => poFile.guid === poDoc.document.guid
		);
		if (lnIndex !== -1) laDocumentsCache.splice(lnIndex, 1);

		StoreHelper.updateDocumentCacheData(this.model(), { documents: laDocumentsCache });
		this.detectChanges();
	}

	public async onRemoveDocumentClickedAsync(poDocument: Document) {
		const loModelCacheData: ICacheData | undefined = StoreHelper.getDocumentCacheData(this.model());
		const laDocumentsCache: IEntityDocumentCache[] = loModelCacheData?.documents ?? [];

		const lnIndex: number = laDocumentsCache.findIndex(
			(poDoc: IEntityDocumentCache) => poDocument._id === poDoc.document._id
		);
		if (lnIndex !== -1) laDocumentsCache.splice(lnIndex, 1);
		else {
			await this.isvcUiMessage.showPopupMessageAsync(
				new ShowMessageParamsPopup({
					header: "Suppression",
					message: `Êtes-vous sûr de vouloir placer ${poDocument.documentName} dans la corbeille ?`,
					buttons: [
						{ text: "Annuler" },
						{
							text: "Supprimer",
							handler: () => {
								laDocumentsCache.push({
									cacheDataState: EEntityDocumentCacheData.Remove,
									document: poDocument,
									path: this.folderPath()
								});
								ArrayHelper.removeElementById(this.documents(), poDocument._id);
							}
						}
					]
				})
			);
		}

		StoreHelper.updateDocumentCacheData(this.model(), { documents: laDocumentsCache });
		this.detectChanges();
	}

	//#region drag-scroll

	public leftBoundStat(reachesLeftBound: boolean): void {
		this.observableHidLeftBtn.value = reachesLeftBound;
		this.detectChanges();
	}

	public rightBoundStat(reachesRightBound: boolean): void {
		this.observableHidRightBtn.value = reachesRightBound;
		this.detectChanges();
	}

	public moveLeft(): void {
		for (let lnIndex = 0; lnIndex < 2; ++lnIndex) {
			this.dragScroll?.moveLeft();
		}
	}

	public moveRight(): void {
		for (let lnIndex = 0; lnIndex < 2; ++lnIndex) {
			this.dragScroll?.moveRight();
		}
	}

	/** Evite les bug de conflits lors du swipe. */
	public disableTouchPropagation(poElementRef: ElementRef<HTMLElement>): void {
		poElementRef.nativeElement.addEventListener("touchmove", (poEvent: TouchEvent) => poEvent.stopPropagation());
		poElementRef.nativeElement.addEventListener("touchstart", (poEvent: TouchEvent) => poEvent.stopPropagation());
		poElementRef.nativeElement.addEventListener("touchend", (poEvent: TouchEvent) => poEvent.stopPropagation());
	}

	//#endregion drag-scroll

	//#endregion METHODS
}
